import { useRef, useState } from 'react';
import { Alert, Button, Flex, Text } from '@backstage/ui';
import { RiFileZipLine, RiFolderOpenLine } from '@remixicon/react';
import { configApiRef, useApi } from '@backstage/frontend-plugin-api';
import {
  formatBytes,
  processFilesToZipBlob,
  processZipFileToZipBlob,
} from '../../lib/projectUpload';
import {
  assessUploadZip,
  type UploadFinding,
} from '@internal/plugin-cloudflare-workers-common';
import { cloudflareWorkersApiRef } from '../../api';
import { WRAP } from '../ui';
import { UploadFindings } from './UploadFindings';

/**
 * Fallback ceiling used when config doesn't set
 * `integrations.cloudflare.vibeUploads.maxUploadBytes` — matches the
 * backend's own default so the two only ever disagree if someone changes one
 * without the other.
 */
export const DEFAULT_MAX_UPLOAD_BYTES = 50 * 1024 * 1024;

const SIZE_LIMIT_HINT =
  'For larger projects, use Claude Code locally with the /make-general:internal-app skill instead.';

export interface UploadControlProps {
  /** Called with the pending-upload id once the zip has been uploaded (or '' when cleared/failed). */
  onChange: (uploadId: string) => void;
  /**
   * Check the upload against the supported app shapes, in the browser before
   * uploading and on the server after (Cloud Agents flow).
   */
  assess?: boolean;
}

/**
 * Folder/zip upload control for the vibe wizards.
 *
 * Zips the selection client-side (via the shared `projectUpload` util, so
 * wizard uploads and raw-template uploads are filtered identically), then
 * POSTs the raw zip bytes to the dedicated upload endpoint and reports back
 * the resulting upload id — the value the scaffolder's `projectUpload`
 * parameter now expects.
 */
export const UploadControl = ({
  onChange,
  assess = false,
}: UploadControlProps) => {
  const configApi = useApi(configApiRef);
  const cloudflareWorkersApi = useApi(cloudflareWorkersApiRef);
  const maxSize =
    configApi.getOptionalNumber(
      'integrations.cloudflare.vibeUploads.maxUploadBytes',
    ) ?? DEFAULT_MAX_UPLOAD_BYTES;

  const folderInputRef = useRef<HTMLInputElement>(null);
  const zipInputRef = useRef<HTMLInputElement>(null);

  const [status, setStatus] = useState<string>();
  const [error, setError] = useState<string>();
  const [findings, setFindings] = useState<UploadFinding[]>([]);
  const [loading, setLoading] = useState(false);

  const reject = (rejected: UploadFinding[]) => {
    setFindings(rejected);
    setStatus(undefined);
    onChange('');
  };

  const handle = async (
    work: () => Promise<{
      blob: Blob;
      sizeBytes: number;
      fileCount: number;
    }>,
  ) => {
    setError(undefined);
    setFindings([]);
    setStatus('Processing…');
    setLoading(true);
    try {
      const { blob, sizeBytes, fileCount } = await work();
      if (sizeBytes > maxSize) {
        setError(
          `Your upload is ${formatBytes(sizeBytes)}, over the ${formatBytes(
            maxSize,
          )} limit. ${SIZE_LIMIT_HINT}`,
        );
        setStatus(undefined);
        onChange('');
        return;
      }

      if (assess) {
        setStatus('Checking your app…');
        const local = await assessUploadZip(blob);
        if (local.verdict === 'rejected') {
          reject(local.findings);
          return;
        }
      }

      setStatus('Uploading…');
      const { uploadId } = await cloudflareWorkersApi.uploadProject(blob);

      if (assess) {
        setStatus('Checking your app…');
        const server = await cloudflareWorkersApi.assessUpload(uploadId);
        if (server.verdict === 'rejected') {
          reject(server.findings);
          return;
        }
        setFindings(server.findings);
      }

      setStatus(`${fileCount} files (${formatBytes(sizeBytes)}) uploaded`);
      onChange(uploadId);
    } catch (err) {
      setError(
        `We couldn't process that upload: ${
          err instanceof Error ? err.message : String(err)
        }`,
      );
      setStatus(undefined);
      onChange('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Flex direction="column" gap="2">
      <input
        ref={folderInputRef}
        type="file"
        style={{ display: 'none' }}
        {...({ webkitdirectory: '', directory: '' } as Record<string, string>)}
        multiple
        onChange={e => {
          const files = e.target.files;
          if (files) handle(() => processFilesToZipBlob(files));
          e.target.value = '';
        }}
      />
      <input
        ref={zipInputRef}
        type="file"
        accept=".zip"
        style={{ display: 'none' }}
        onChange={e => {
          const file = e.target.files?.[0];
          if (file) handle(() => processZipFileToZipBlob(file));
          e.target.value = '';
        }}
      />
      <Flex gap="2" style={WRAP}>
        <Button
          variant="secondary"
          iconStart={<RiFolderOpenLine size={14} aria-hidden />}
          onPress={() => folderInputRef.current?.click()}
          isDisabled={loading}
        >
          Select folder
        </Button>
        <Button
          variant="secondary"
          iconStart={<RiFileZipLine size={14} aria-hidden />}
          onPress={() => zipInputRef.current?.click()}
          isDisabled={loading}
        >
          Select zip
        </Button>
      </Flex>
      {status && !error && (
        <Text variant="body-medium" color="secondary">
          {status}
        </Text>
      )}
      {error && <Alert status="danger" icon title={error} />}
      <UploadFindings findings={findings} />
    </Flex>
  );
};
